import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import { format, formatDistanceToNow } from 'date-fns';
import { useStaffMemberInfoContext } from '@/pages/StaffMemberInfo/StaffMemberInfo.context';
import { Candidate } from '@/services/candidates';
import { StaffMember } from '@/types/api/staffMember';
import { getMostRecentStaffMemberActiveJob } from '@/utils/staffMember';
import * as Styles from './StaffResumeHeader.styles';
import CandidateProfilePictureManager from '../CandidateProfilePictureManager';
import Menu from '../Menu';

export type StaffResumeHeaderType = Candidate;

type StaffResumeHeaderComponentProps = {
  staffMember?: StaffMember;
  onDeleteClick?: () => void;
  onSetInactiveClick?: () => void;
  onSetActiveClick?: () => void;
};

const StaffResumeHeaderComponent = function ({
  staffMember,
  onDeleteClick,
  onSetInactiveClick,
  onSetActiveClick,
}: StaffResumeHeaderComponentProps): JSX.Element {
  const { refreshUser, directLeader } = useStaffMemberInfoContext();

  const mostRecentActiveJob = getMostRecentStaffMemberActiveJob(staffMember?.jobInfo || []);
  const mostRecentDirectLeaderActiveJob = getMostRecentStaffMemberActiveJob(
    directLeader?.jobInfo || []
  );

  const staffMemberHireDate = staffMember?.contract?.hireDate
    ? new Date(staffMember?.contract?.hireDate)
    : undefined;

  return (
    <Styles.Box>
      {staffMember ? (
        <>
          <Styles.ContainerSection>
            <Styles.Info data-testid="candidate-info">
              <CandidateProfilePictureManager
                firstName={staffMember.person.firstName}
                lastName={staffMember.person.lastName}
                pictureUrl={staffMember.person.pictureUrl ?? ''}
                refetchCandidate={refreshUser}
                pictureIsEditable={Boolean(staffMember.person.id)}
                id={staffMember.person.id || ''}
              />
              <Styles.InfoResume>
                <Styles.Info>
                  <Styles.CandidateName data-testid="candidate-name">
                    {staffMember.person.firstName} {staffMember.person.lastName}
                  </Styles.CandidateName>
                  {staffMember.person.employeeWorkStatus?.toLowerCase() === 'inactive' && (
                    <span
                      style={{
                        display: 'inline-block',
                        marginLeft: 8,
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 500,
                        backgroundColor: '#FEE2E2',
                        color: '#DC2626',
                        border: '1px solid #FECACA',
                        verticalAlign: 'middle',
                      }}
                    >
                      Inactive
                    </span>
                  )}
                </Styles.Info>
                <Styles.Info>
                  <Styles.SimpleText
                    data-testid="candidate-developer-type"
                    className="tw-capitalize"
                  >
                    {mostRecentActiveJob?.title}
                  </Styles.SimpleText>
                </Styles.Info>
              </Styles.InfoResume>
            </Styles.Info>
          </Styles.ContainerSection>

          <Styles.ContainerSection>
            {mostRecentActiveJob?.area && (
              <Styles.SimpleText>
                <MapsHomeWorkOutlinedIcon />
                {mostRecentActiveJob.area}
              </Styles.SimpleText>
            )}
            {mostRecentActiveJob?.team?.name && (
              <Styles.SimpleText>
                <WorkspacesOutlinedIcon />
                {mostRecentActiveJob.team.name}
              </Styles.SimpleText>
            )}
          </Styles.ContainerSection>

          <Styles.ContainerSection>
            {staffMember.person.email && (
              <Styles.SimpleText>{staffMember.person.email}</Styles.SimpleText>
            )}
            {staffMember.person.mobilePhone && (
              <Styles.SimpleText>{staffMember.person.mobilePhone}</Styles.SimpleText>
            )}
          </Styles.ContainerSection>

          <Styles.ContainerSection>
            <Styles.SmallerText $bold>Hire Date</Styles.SmallerText>
            <Styles.SmallerText>
              {staffMemberHireDate ? format(staffMemberHireDate, 'MMM d, yyyy') : '---'}
            </Styles.SmallerText>
            {staffMemberHireDate && (
              <Styles.SmallerText className="tw-capitalize">
                {formatDistanceToNow(staffMemberHireDate)}
              </Styles.SmallerText>
            )}
          </Styles.ContainerSection>

          <Styles.ContainerSection>
            <div className="tw-flex tw-items-center">
              <div className="tw-grow">
                <Styles.SmallerText $bold>Direct leader</Styles.SmallerText>
                {directLeader?.person ? (
                  <Styles.Info>
                    <Styles.ManagerPicture
                      className="mr-0"
                      pictureUrl={directLeader.person.pictureUrl || undefined}
                      firstName={directLeader.person.firstName}
                      lastName={directLeader.person.lastName}
                      size={24}
                    />
                    <Styles.InfoResume>
                      <Styles.ManagerName>
                        {directLeader.person.firstName} {directLeader.person.lastName}
                      </Styles.ManagerName>
                      {mostRecentDirectLeaderActiveJob?.title && (
                        <Styles.SmallerText>
                          {mostRecentDirectLeaderActiveJob?.title}
                        </Styles.SmallerText>
                      )}
                    </Styles.InfoResume>
                  </Styles.Info>
                ) : (
                  <Styles.SmallerText>---</Styles.SmallerText>
                )}
              </div>
              <Menu
                icon={MoreHorizIcon}
                menuId="staff-member-actions"
                menuItems={[
                  staffMember.person.employeeWorkStatus?.toLowerCase() === 'inactive'
                    ? {
                        label: 'Set as Active',
                        menuItemIcon: CheckCircleOutlineIcon,
                        callback: (e: React.MouseEvent) => {
                          e.stopPropagation();
                          onSetActiveClick?.();
                        },
                      }
                    : {
                        label: 'Set as Inactive',
                        menuItemIcon: BlockIcon,
                        callback: (e: React.MouseEvent) => {
                          e.stopPropagation();
                          onSetInactiveClick?.();
                        },
                      },
                  {
                    label: 'Delete',
                    menuItemOptions: { warning: true },
                    menuItemIcon: DeleteIcon,
                    callback: (e: React.MouseEvent) => {
                      e.stopPropagation();
                      onDeleteClick?.();
                    },
                  },
                ]}
              />
            </div>
          </Styles.ContainerSection>
        </>
      ) : (
        <div className="tw-flex tw-items-center">
          <SkeletonCircle w={12} h={12} className="tw-mr-3" />
          <div>
            <Skeleton className="tw-h-4 tw-mb-1.5 tw-w-[100px]" />
            <div className="tw-flex tw-gap-3 tw-items-center">
              <Skeleton className="tw-w-10 tw-h-[18px]" />
              <Skeleton className="tw-w-[100px] tw-h-3.5" />
            </div>
          </div>
        </div>
      )}
    </Styles.Box>
  );
};

export default StaffResumeHeaderComponent;
